// components/FileUploadSection.js - Clean and Simple
import React, { useState, useEffect } from 'react';
import { formatFileSizeMB } from '../utils/fileUtils';
import { uploadFileToServer } from '../utils/fileUploadUtils';
import { useAuth } from '../../../hooks/useAuth';

const FileUploadSection = ({ config, onFileChange, error }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadError, setUploadError] = useState(null);
  const { token } = useAuth();

  // Figure out what file types are allowed from config
  const getAllowedRules = () => {
    if (!config?.configuration?.files?.options) return null;
    
    let allowsPdf = false;
    let allowsImages = false;
    let maxPdfFiles = 1;
    let maxImageFiles = 3;
    let maxPdfSize = 10;
    let maxImageSize = 5;
    
    config.configuration.files.options.forEach(option => {
      if (option.types.includes('pdf')) {
        allowsPdf = true;
        maxPdfFiles = option.maxFiles || 1;
        maxPdfSize = option.maxSizeMB || 10;
      }
      if (option.types.some(type => ['jpg', 'jpeg', 'png', 'webp', 'heic'].includes(type))) {
        allowsImages = true;
        maxImageFiles = option.maxFiles || 3;
        maxImageSize = option.maxSizeMB || 5;
      }
    });
    
    return { allowsPdf, allowsImages, maxPdfFiles, maxImageFiles, maxPdfSize, maxImageSize };
  };

  const rules = getAllowedRules();
  
  if (!rules) return null;

  // Get current file type mode (pdf or images) based on uploaded files
  const getCurrentMode = () => {
    if (uploadedFiles.length === 0) return null;
    const firstFile = uploadedFiles[0];
    return firstFile.contentType === 'application/pdf' ? 'pdf' : 'images';
  };

  const currentMode = getCurrentMode();

  // Get instructions for user
  const getInstructions = () => {
    const options = [];
    if (rules.allowsPdf) {
      options.push(`${rules.maxPdfFiles} PDF`);
    }
    if (rules.allowsImages) {
      options.push(`up to ${rules.maxImageFiles} images`);
    }
    return `Upload ${options.join(' OR ')}`;
  };

  // Get accepted file types for input
  const getAcceptTypes = () => {
    const types = [];
    if (rules.allowsPdf && (!currentMode || currentMode === 'pdf')) {
      types.push('.pdf');
    }
    if (rules.allowsImages && (!currentMode || currentMode === 'images')) {
      types.push('.jpg', '.jpeg', '.png', '.webp', '.heic');
    }
    return types.join(',');
  };

  // Check if we can upload more files
  const canUploadMore = () => {
    if (currentMode === 'pdf') {
      return uploadedFiles.length < rules.maxPdfFiles;
    }
    if (currentMode === 'images') {
      return uploadedFiles.length < rules.maxImageFiles;
    }
    return true; // No files yet, can upload anything
  };

  // Validate a single file
  const validateFile = (file) => {
    const isPdf = file.type === 'application/pdf';
    const isImage = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic'].includes(file.type);
    
    // Check if file type is supported
    if (!isPdf && !isImage) {
      return { valid: false, error: 'File type not supported' };
    }
    
    // Check mode compatibility
    if (currentMode === 'pdf' && !isPdf) {
      return { valid: false, error: 'Only PDF files allowed (you already uploaded a PDF)' };
    }
    if (currentMode === 'images' && isPdf) {
      return { valid: false, error: 'Only images allowed (you already uploaded images)' };
    }
    
    // Check file count limits
    const maxFiles = isPdf ? rules.maxPdfFiles : rules.maxImageFiles;
    if (uploadedFiles.length >= maxFiles) {
      return { valid: false, error: `Maximum ${maxFiles} file${maxFiles > 1 ? 's' : ''} allowed` };
    }
    
    // Check file size
    const maxSize = isPdf ? rules.maxPdfSize : rules.maxImageSize;
    const fileSizeMB = file.size / 1024 / 1024;
    if (fileSizeMB > maxSize) {
      return { valid: false, error: `File too large (max ${maxSize}MB)` };
    }
    
    return { valid: true };
  };

  // Handle file selection
  const handleFileSelection = async (selectedFiles) => {
    const fileArray = Array.from(selectedFiles);
    setUploadError(null);
    
    for (const file of fileArray) {
      await uploadSingleFile(file);
    }
  };

  // Upload a single file
  const uploadSingleFile = async (file) => {
    const fileId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Validate file
    const validation = validateFile(file);
    if (!validation.valid) {
      setUploadError(validation.error);
      return;
    }
    
    try {
      // Add to uploading state
      setUploadingFiles(prev => [...prev, {
        id: fileId,
        name: file.name,
        size: file.size
      }]);
      
      // Upload to server
      const result = await uploadFileToServer(
        file,
        token,
        (progress) => {
          setUploadProgress(prev => ({ ...prev, [fileId]: progress }));
        }
      );
      
      // Create file info
      const fileInfo = {
        id: fileId,
        url: result.url,
        originalFileName: file.name,
        contentType: file.type,
        fileSizeBytes: file.size,
        uploadedAt: Date.now()
      };
      
      // Add to uploaded files
      setUploadedFiles(prev => [...prev, fileInfo]);
      
      // Remove from uploading
      setUploadingFiles(prev => prev.filter(f => f.id !== fileId));
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[fileId];
        return newProgress;
      });
      
      // Notify parent
      const allFiles = [...uploadedFiles, fileInfo];
      onFileChange({
        files: allFiles.map(f => ({
          name: f.originalFileName,
          size: f.fileSizeBytes,
          type: f.contentType,
          url: f.url
        }))
      });
      
    } catch (error) {
      console.error('Upload failed:', error);
      
      // Remove from uploading
      setUploadingFiles(prev => prev.filter(f => f.id !== fileId));
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[fileId];
        return newProgress;
      });
      
      setUploadError(`Failed to upload ${file.name}: ${error.message}`);
    }
  };

  // Remove a file
  const removeFile = (fileId) => {
    const updatedFiles = uploadedFiles.filter(f => f.id !== fileId);
    setUploadedFiles(updatedFiles);
    
    // Notify parent
    onFileChange({
      files: updatedFiles.map(f => ({
        name: f.originalFileName,
        size: f.fileSizeBytes,
        type: f.contentType,
        url: f.url
      }))
    });
  };

  return (
    <div className="mb-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Documents</h3>
      
      <div className={`border-2 border-dashed rounded-lg p-6 text-center ${
        error || uploadError ? 'border-red-500' : 'border-gray-300'
      }`}>
        
        {/* Upload Button */}
        {canUploadMore() && (
          <>
            <input
              type="file"
              multiple={currentMode !== 'pdf'}
              accept={getAcceptTypes()}
              onChange={(e) => handleFileSelection(e.target.files)}
              className="hidden"
              id="file-upload"
            />
            <label 
              htmlFor="file-upload"
              className="cursor-pointer inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 mr-2"
            >
              Choose Files
            </label>
            <button 
              type="button" 
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Take Photo
            </button>
          </>
        )}
        
        {!canUploadMore() && (
          <p className="text-gray-500">Maximum files uploaded</p>
        )}
        
        {/* Instructions */}
        <div className="mt-4 text-sm text-gray-600">
          <p>{getInstructions()}</p>
        </div>
        
        {/* Currently Uploading */}
        {uploadingFiles.map((file) => (
          <div key={file.id} className="flex items-center justify-between bg-yellow-50 px-3 py-2 rounded-md mb-2 mt-4">
            <span className="text-sm text-gray-700">{file.name}</span>
            <div className="flex items-center">
              <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${uploadProgress[file.id] || 0}%` }}
                ></div>
              </div>
              <span className="text-xs text-gray-500">
                {Math.round(uploadProgress[file.id] || 0)}%
              </span>
            </div>
          </div>
        ))}
        
        {/* Uploaded Files */}
        {uploadedFiles.map((file) => (
          <div key={file.id} className="flex items-center justify-between bg-green-50 px-3 py-2 rounded-md mb-2 mt-4">
            <div className="flex items-center">
              <span className="text-green-600 mr-2">✓</span>
              <span className="text-sm text-gray-700">{file.originalFileName}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs text-gray-500 mr-2">
                {formatFileSizeMB(file.fileSizeBytes)} MB
              </span>
              <button
                onClick={() => removeFile(file.id)}
                className="text-red-500 hover:text-red-700 text-sm"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        
        {/* Errors */}
        {(uploadError || error) && (
          <div className="mt-4 text-sm text-red-600 bg-red-50 p-2 rounded">
            {uploadError || error}
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploadSection;